import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowRepeat,
  CheckCircleFill,
  ChevronDown,
  ChevronRight,
  Clipboard,
  DashCircle,
  ExclamationTriangleFill,
  Search,
  XCircleFill,
} from "react-bootstrap-icons";
import moment from "moment";
import "react-toastify/dist/ReactToastify.css";
import "../assets/css/DeviceHealthCheck.css";
import { getDeviceHealth } from "../commonApi";
import { Loader } from "../Loader";
import MqttTopicsTab from "./MqttTopicsTab";

// Read-only device health check: DB registration, AWS IoT Thing, certificate, policies,
// commissioning, connectivity, shadow and last data (GET /things/{thingName}/health),
// plus the device's MQTT topics checked against its IoT policies (GET /things/{thingName}/mqtt-topics).

const STATUS = {
  PASS: { cls: "is-pass", label: "Healthy", Icon: CheckCircleFill, color: "#198754" },
  WARN: { cls: "is-warn", label: "Warning", Icon: ExclamationTriangleFill, color: "#b7791f" },
  FAIL: { cls: "is-fail", label: "Failing", Icon: XCircleFill, color: "#d63c4a" },
  SKIPPED: { cls: "is-skipped", label: "Skipped", Icon: DashCircle, color: "#6c757d" },
};

// Short labels for the lifecycle bar, in device-lifecycle order (same order the API returns).
const STEP_LABEL = {
  REGISTRATION: "Registered",
  THING: "IoT Thing",
  CERTIFICATE: "Certificate",
  POLICY: "Policy",
  COMMISSIONING: "Commissioned",
  CONNECTIVITY: "Online",
  SHADOW: "Shadow",
  LAST_DATA: "Data",
};

const TABS = [
  ["checks", "Checks"],
  ["commissioning", "Commissioning"],
  ["shadow", "Shadow"],
  ["mqtt", "MQTT Topics"],
];

const statusOf = (s) => STATUS[s] || STATUS.SKIPPED;

const StatusIcon = ({ status, size = 18 }) => {
  const s = statusOf(status);
  return <s.Icon size={size} color={s.color} title={s.label} />;
};

// commissioning timestamps are epoch ms; shadow timestamps are epoch seconds
const toMs = (ts) => (ts < 1e12 ? ts * 1000 : ts);
const formatTs = (ts) => (ts ? moment(toMs(ts)).format("YYYY-MM-DD HH:mm:ss") : "—");
const fromNow = (ts) => (ts ? moment(toMs(ts)).fromNow() : "");

const humanize = (key) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase())
    .replace(/\bIn Db\b/, "in DB");

const isTimeKey = (key) => /epoch|since|timestamp|checkedAt/i.test(key);

// A commissioning step failed when the status says so or carries an HTTP-style error code (e.g. 500).
const isFailedStep = (code) => {
  const c = String(code ?? "").trim();
  return /FAIL|ERROR|EXCEPTION|REJECT/i.test(c) || (/^\d+$/.test(c) && Number(c) >= 400);
};

const elapsed = (from, to) => {
  if (!from || !to) return "";
  const secs = Math.max(0, Math.round((toMs(to) - toMs(from)) / 1000));
  if (secs < 60) return `+${secs}s`;
  const mins = Math.floor(secs / 60);
  return mins < 60 ? `+${mins}m ${secs % 60}s` : `+${Math.floor(mins / 60)}h ${mins % 60}m`;
};

const reportText = (report) =>
  [
    `Device health check: ${report.thingName}`,
    `Model: ${report.modelName || "—"}${report.parentThingName ? ` | Hub: ${report.parentThingName}` : ""}`,
    `Overall: ${report.overall} | Checked: ${formatTs(report.checkedAt)}`,
    "",
    ...report.checks.map((c) => `[${c.status}] ${c.title}: ${c.message || ""}`),
  ].join("\n");

// ---------------------------------------------------------------- header

const ScoreRing = ({ passed, total, status }) => {
  const r = 32;
  const circumference = 2 * Math.PI * r;
  const fraction = total ? passed / total : 0;
  return (
    <div className="dhc-ring">
      <svg width="76" height="76">
        <circle cx="38" cy="38" r={r} fill="none" stroke="#eceef2" strokeWidth="7" />
        <circle
          cx="38"
          cy="38"
          r={r}
          fill="none"
          stroke={statusOf(status).color}
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={`${circumference * fraction} ${circumference}`}
        />
      </svg>
      <div className="dhc-ring-label">
        <strong>
          {passed}/{total}
        </strong>
        <span>passed</span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- checks tab

const DetailValue = ({ name, value, onCopy }) => {
  if (value === null || value === undefined || value === "") return <span className="dhc-muted">—</span>;
  if (typeof value === "boolean") return <span>{value ? "Yes" : "No"}</span>;
  if (typeof value === "number" && isTimeKey(name)) {
    return (
      <span>
        {formatTs(value)} <span className="dhc-muted">({fromNow(value)})</span>
      </span>
    );
  }
  if (Array.isArray(value)) {
    if (!value.length) return <span className="dhc-muted">none</span>;
    return (
      <div className="dhc-chips">
        {value.map((v, i) => (
          <span className="dhc-chip" key={i}>
            {typeof v === "object" ? JSON.stringify(v) : String(v)}
          </span>
        ))}
      </div>
    );
  }
  if (typeof value === "object") {
    if (!Object.keys(value).length) return <span className="dhc-muted">—</span>;
    return <pre className="dhc-raw" style={{ margin: 0 }}>{JSON.stringify(value, null, 2)}</pre>;
  }
  const text = String(value);
  if (text.startsWith("arn:")) {
    return (
      <span className="dhc-mono">
        {text}{" "}
        <button type="button" className="dhc-icon-btn" title="Copy" onClick={() => onCopy(text)}>
          <Clipboard size={12} />
        </button>
      </span>
    );
  }
  return <span>{text}</span>;
};

const CheckRow = ({ check, focus, onCopy }) => {
  const [open, setOpen] = useState(check.status === "FAIL");
  const [raw, setRaw] = useState(false);
  const [flash, setFlash] = useState(false);
  const ref = useRef(null);
  const details = check.details || {};
  const hasDetails = Object.keys(details).length > 0;

  useEffect(() => {
    if (focus && focus.key === check.key) {
      if (hasDetails) setOpen(true);
      setFlash(true);
      ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      const t = setTimeout(() => setFlash(false), 1300);
      return () => clearTimeout(t);
    }
  }, [focus, check.key, hasDetails]);

  return (
    <div ref={ref} className={`dhc-check ${statusOf(check.status).cls} ${flash ? "is-flash" : ""}`}>
      <div
        className={`dhc-check-head ${hasDetails ? "" : "is-static"}`}
        onClick={() => hasDetails && setOpen(!open)}
      >
        <span className="dhc-muted">{hasDetails && (open ? <ChevronDown /> : <ChevronRight />)}</span>
        <StatusIcon status={check.status} />
        <span className="dhc-check-title">{check.title}</span>
        <span className="dhc-check-msg">{check.message}</span>
        <span className="dhc-duration">{check.durationMs ? `${check.durationMs} ms` : ""}</span>
      </div>
      {open && hasDetails && (
        <div className="dhc-check-body">
          {raw ? (
            <pre className="dhc-raw">{JSON.stringify(details, null, 2)}</pre>
          ) : (
            <div className="dhc-kv">
              {Object.entries(details).map(([k, v]) => (
                <React.Fragment key={k}>
                  <div className="dhc-kv-key">{humanize(k)}</div>
                  <div>
                    <DetailValue name={k} value={v} onCopy={onCopy} />
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}
          <button type="button" className="dhc-link dhc-toggle-raw" onClick={() => setRaw(!raw)}>
            {raw ? "Show as table" : "Show raw JSON"}
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- commissioning tab

const CommissioningTab = ({ steps }) => {
  if (!steps?.length) return <div className="dhc-empty">No commissioning log entries for this device</div>;
  return (
    <ol className="dhc-timeline">
      {steps.map((s, i) => {
        const failed = isFailedStep(s.statusCode);
        const showDesc = s.description && s.description.trim() !== String(s.statusCode ?? "").trim();
        return (
          <li key={i} className={`dhc-tl-item ${failed ? "is-fail" : ""}`}>
            <div className="dhc-tl-time">
              {formatTs(s.timestamp)}
              {i > 0 && <small>{elapsed(steps[i - 1].timestamp, s.timestamp)}</small>}
            </div>
            <div className="dhc-tl-rail">
              <span className="dhc-tl-dot">{failed ? <XCircleFill size={13} /> : <CheckCircleFill size={13} />}</span>
            </div>
            <div className="dhc-tl-content">
              <div className="dhc-tl-status">{s.statusCode}</div>
              <div className="dhc-tl-component">
                {s.component}
                {s.sessionId ? ` · session ${s.sessionId}` : ""}
              </div>
              {showDesc && <div className="dhc-tl-desc">{s.description}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

// ---------------------------------------------------------------- shadow tab

const isPlainObject = (v) => v && typeof v === "object" && !Array.isArray(v);

// {a: {b: 1}} -> {"a.b": 1}; arrays and primitives are leaves
const flatten = (obj, prefix = "", out = {}) => {
  if (!isPlainObject(obj)) return out;
  Object.entries(obj).forEach(([k, v]) => {
    const path = prefix ? `${prefix}.${k}` : k;
    if (isPlainObject(v) && Object.keys(v).length) flatten(v, path, out);
    else out[path] = v;
  });
  return out;
};

const latestTimestamp = (node) => {
  if (Array.isArray(node)) return Math.max(0, ...node.map(latestTimestamp));
  if (!isPlainObject(node)) return 0;
  return Math.max(
    typeof node.timestamp === "number" ? node.timestamp : 0,
    ...Object.entries(node)
      .filter(([k]) => k !== "timestamp")
      .map(([, v]) => latestTimestamp(v))
  );
};

// metadata.reported mirrors reported, with {timestamp} at each leaf -> {"a.b": epochSeconds}
const flattenMeta = (node, prefix = "", out = {}) => {
  if (Array.isArray(node)) {
    out[prefix] = latestTimestamp(node);
    return out;
  }
  if (!isPlainObject(node)) return out;
  const keys = Object.keys(node).filter((k) => k !== "timestamp");
  if (!keys.length && typeof node.timestamp === "number") {
    out[prefix] = node.timestamp;
    return out;
  }
  keys.forEach((k) => flattenMeta(node[k], prefix ? `${prefix}.${k}` : k, out));
  return out;
};

const show = (v) => {
  if (v === undefined) return <span className="dhc-muted">—</span>;
  if (typeof v === "string") return v;
  return <span className="dhc-mono">{JSON.stringify(v)}</span>;
};

const ShadowTab = ({ shadow }) => {
  const [query, setQuery] = useState("");
  const [raw, setRaw] = useState(false);

  const rows = useMemo(() => {
    if (!shadow) return [];
    const reported = flatten(shadow.reported);
    const desired = flatten(shadow.desired);
    const delta = flatten(shadow.delta);
    const meta = { ...flattenMeta(shadow.metadata?.desired), ...flattenMeta(shadow.metadata?.reported) };
    const keys = Array.from(new Set([...Object.keys(reported), ...Object.keys(desired)])).sort();
    return keys.map((k) => ({
      key: k,
      reported: reported[k],
      desired: desired[k],
      updated: meta[k],
      inDelta: Object.keys(delta).some((d) => d === k || k.startsWith(`${d}.`) || d.startsWith(`${k}.`)),
    }));
  }, [shadow]);

  if (!shadow) return <div className="dhc-empty">No shadow for this device</div>;

  const q = query.trim().toLowerCase();
  const visible = q ? rows.filter((r) => r.key.toLowerCase().includes(q)) : rows;
  const deltaCount = rows.filter((r) => r.inDelta).length;

  return (
    <>
      <div className="dhc-toolbar">
        <div className="dhc-search-field">
          <Search size={13} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter keys" />
        </div>
        <div className="dhc-segment">
          <button type="button" className={!raw ? "is-active" : ""} onClick={() => setRaw(false)}>
            Table
          </button>
          <button type="button" className={raw ? "is-active" : ""} onClick={() => setRaw(true)}>
            Raw JSON
          </button>
        </div>
        <div className="dhc-stats">
          <span className="dhc-pill is-info">Version {shadow.version}</span>
          <span className="dhc-pill is-skipped">{rows.length} keys</span>
          <span className={`dhc-pill ${deltaCount ? "is-warn" : "is-pass"}`}>
            {deltaCount ? `${deltaCount} pending delta` : "In sync"}
          </span>
        </div>
      </div>

      {raw ? (
        <pre className="dhc-raw" style={{ maxHeight: 560 }}>
          {JSON.stringify({ reported: shadow.reported, desired: shadow.desired, delta: shadow.delta }, null, 2)}
        </pre>
      ) : (
        <div className="dhc-table-wrap">
          <table className="dhc-table">
            <thead>
              <tr>
                <th>Key</th>
                <th>Reported</th>
                <th>Desired</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.key} className={r.inDelta ? "is-delta" : ""}>
                  <td className="dhc-mono">{r.key}</td>
                  <td>{show(r.reported)}</td>
                  <td>{show(r.desired)}</td>
                  <td title={formatTs(r.updated)} className="dhc-muted" style={{ whiteSpace: "nowrap" }}>
                    {r.updated ? fromNow(r.updated) : "—"}
                  </td>
                </tr>
              ))}
              {!visible.length && (
                <tr>
                  <td colSpan={4} className="dhc-muted">
                    No keys match
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

// ---------------------------------------------------------------- page

function DeviceHealthCheck() {
  const { thingName: routeThingName } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === searchParams.get("tab")) ? searchParams.get("tab") : "checks";

  const [input, setInput] = useState(routeThingName || "");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [focus, setFocus] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const runCheck = useCallback(async (thingName) => {
    if (!thingName) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getDeviceHealth(thingName);
      setReport(res.data?.data || null);
    } catch (e) {
      setReport(null);
      setError(
        e.response?.status === 403
          ? "You need the ADMIN role to run a device health check"
          : e.response?.data?.error?.message || e.message
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setInput(routeThingName || "");
    if (routeThingName) runCheck(routeThingName);
    else setReport(null);
  }, [routeThingName, runCheck]);

  const setTab = (key) => {
    const next = new URLSearchParams(searchParams);
    if (key === "checks") next.delete("tab");
    else next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  const openThing = (name) => {
    const qs = searchParams.get("tab") ? `?tab=${searchParams.get("tab")}` : "";
    navigate(`/devices/health/${encodeURIComponent(name)}${qs}`);
  };

  const rerun = () => {
    runCheck(routeThingName);
    setReloadKey((k) => k + 1);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const name = input.trim();
    if (!name) return;
    if (name === routeThingName) rerun();
    else openThing(name);
  };

  const copy = async (text, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  const goToCheck = (key) => {
    setTab("checks");
    setFocus({ key, at: Date.now() });
  };

  // AWS doesn't track online status in this account: hide the row and the "Online" step instead of showing "Skipped"
  const checks = (report?.checks || []).filter((c) => !(c.key === "CONNECTIVITY" && c.status === "SKIPPED"));
  const counted = checks.filter((c) => c.status !== "SKIPPED");
  const passed = counted.filter((c) => c.status === "PASS").length;
  const overall = report ? statusOf(report.overall) : null;
  const failedSteps = (report?.commissioningTimeline || []).filter((s) => isFailedStep(s.statusCode)).length;

  return (
    <div className="dhc">
      <form className="dhc-search" onSubmit={onSubmit}>
        <div className="dhc-search-field">
          <Search size={14} />
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Thing name, e.g. sh__dev__curtain__3b1f13bc-..."
          />
        </div>
        <button className="dhc-btn dhc-btn-primary" type="submit" disabled={loading || !input.trim()}>
          Run check
        </button>
      </form>

      {!routeThingName && !loading && (
        <div className="dhc-panel dhc-empty">
          Enter a device or hub thing name to check its registration, certificate, policies, connectivity, shadow and
          MQTT topics.
        </div>
      )}

      {loading && (
        <div className="spin-loader">
          <Loader />
        </div>
      )}

      {!loading && error && (
        <div className="dhc-alert dhc-alert-fail">
          <XCircleFill style={{ marginTop: 3, flexShrink: 0 }} />
          <span>{error}</span>
        </div>
      )}

      {!loading && report && (
        <>
          <div className={`dhc-panel dhc-hero ${overall.cls}`}>
            <ScoreRing passed={passed} total={counted.length} status={report.overall} />
            <div className="dhc-hero-main">
              <div className="dhc-hero-title">
                <h2>{report.thingName}</h2>
                <button type="button" className="dhc-icon-btn" title="Copy thing name" onClick={() => copy(report.thingName)}>
                  <Clipboard size={13} />
                </button>
                <span className={`dhc-pill ${overall.cls}`}>
                  <overall.Icon size={11} /> {overall.label}
                </span>
              </div>
              <div className="dhc-meta">
                <span>
                  Model <b>{report.modelName || "—"}</b>
                </span>
                <span>
                  Device ID <b>{report.deviceId ?? "—"}</b>
                </span>
                {report.parentThingName && (
                  <span>
                    Hub{" "}
                    <button type="button" className="dhc-link dhc-mono" onClick={() => openThing(report.parentThingName)}>
                      {report.parentThingName}
                    </button>
                  </span>
                )}
                <span>
                  Checked <b>{formatTs(report.checkedAt)}</b>
                </span>
              </div>
            </div>
            <div className="dhc-hero-actions">
              <button type="button" className="dhc-btn" onClick={rerun}>
                <ArrowRepeat size={14} /> Re-run
              </button>
              <button type="button" className="dhc-btn" onClick={() => copy(reportText(report), "Report copied")}>
                <Clipboard size={14} /> Copy report
              </button>
            </div>
          </div>

          <div className="dhc-panel">
            <div className="dhc-lifecycle">
              {checks.map((c) => (
                <button
                  type="button"
                  key={c.key}
                  className={`dhc-step ${statusOf(c.status).cls}`}
                  title={`${c.title}: ${c.message || ""}`}
                  onClick={() => goToCheck(c.key)}
                >
                  <span className="dhc-step-dot">
                    {React.createElement(statusOf(c.status).Icon, { size: 14 })}
                  </span>
                  <span className="dhc-step-label">{STEP_LABEL[c.key] || c.title}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="dhc-panel">
            <div className="dhc-tabs">
              {TABS.map(([key, label]) => (
                <button
                  type="button"
                  key={key}
                  className={`dhc-tab ${tab === key ? "is-active" : ""}`}
                  onClick={() => setTab(key)}
                >
                  {label}
                  {key === "checks" && <span className="dhc-tab-count">{checks.length}</span>}
                  {key === "commissioning" && (
                    <span className={`dhc-tab-count ${failedSteps ? "dhc-pill is-fail" : ""}`}>
                      {failedSteps ? `${failedSteps} failed` : report.commissioningTimeline?.length || 0}
                    </span>
                  )}
                  {key === "shadow" && report.shadow && <span className="dhc-tab-count">v{report.shadow.version}</span>}
                </button>
              ))}
            </div>
            <div className="dhc-tab-body">
              {tab === "checks" &&
                checks.map((c) => <CheckRow key={c.key} check={c} focus={focus} onCopy={copy} />)}
              {tab === "commissioning" && <CommissioningTab steps={report.commissioningTimeline} />}
              {tab === "shadow" && <ShadowTab shadow={report.shadow} />}
              {tab === "mqtt" && (
                <MqttTopicsTab thingName={report.thingName} reloadKey={reloadKey} onCopy={(t) => copy(t, "Topic copied")} />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default DeviceHealthCheck;
